import { Select } from "antd";
import { useMemo, useState } from "react";
import { useGetUnitOfMeasurements } from "../../api/unit-of-measurement";
import { buildSearchableDropdownRender } from "../ui/searchable-select-dropdown";

type UnitOfMeasurementSelectProps = {
  value?: number;
  onChange?: (value: number) => void;
  status?: "error" | "warning";
  placeholder?: string;
  disabled?: boolean;
};

function formatUomLabel(name: string, symbol?: string | null) {
  return symbol ? `${name} (${symbol})` : name;
}

export function UnitOfMeasurementSelect({
  value,
  onChange,
  status,
  placeholder = "Select unit of measurement",
  disabled,
}: UnitOfMeasurementSelectProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const { data: uoms = [], isLoading } = useGetUnitOfMeasurements({
    activeOnly: true,
  });

  const options = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return uoms
      .filter((u) => {
        if (!term) return true;
        return (
          u.name.toLowerCase().includes(term) ||
          u.code.toLowerCase().includes(term) ||
          (u.symbol || "").toLowerCase().includes(term)
        );
      })
      .map((u) => ({
        label: formatUomLabel(u.name, u.symbol),
        value: u.id,
      }));
  }, [uoms, searchTerm]);

  const dropdownRender = buildSearchableDropdownRender({
    searchPlaceholder: "Search units of measurement...",
    onSearch: setSearchTerm,
    isLoading,
    hasMore: false,
    optionCount: options.length,
  });

  return (
    <Select
      value={value || undefined}
      onChange={onChange}
      placeholder={placeholder}
      status={status}
      disabled={disabled}
      loading={isLoading}
      style={{ width: "100%" }}
      options={options}
      showSearch={false}
      filterOption={false}
      dropdownRender={dropdownRender}
      onDropdownVisibleChange={(visible) => {
        if (!visible) setSearchTerm("");
      }}
      notFoundContent={isLoading ? "Loading..." : "No units found"}
    />
  );
}
